import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');
    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    useEffect(() => {
        if (!token) {
            setError('Invalid reset link. Please request a new password reset.');
        }
    }, [token]);
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (error)
            setError(null);
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        // Validation
        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            setLoading(false);
            return;
        }
        if (formData.password.length < 8) {
            setError('Password must be at least 8 characters long');
            setLoading(false);
            return;
        }
        if (!token) {
            setError('Invalid reset token');
            setLoading(false);
            return;
        }
        try {
            const response = await fetch('/api/auth/reset-password', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    token: token,
                    password: formData.password
                }),
            });
            const data = await response.json();
            if (data.success) {
                setSuccess(true);
                // Redirect to login after 3 seconds
                setTimeout(() => {
                    navigate('/login', {
                        state: { message: 'Password reset successful! Please log in with your new password.' }
                    });
                }, 3000);
            }
            else {
                setError(data.error || 'Failed to reset password');
            }
        }
        catch (err) {
            setError('Failed to reset password. Please try again.');
        }
        finally {
            setLoading(false);
        }
    };
    const getPasswordStrengthColor = () => {
        const password = formData.password;
        if (password.length === 0)
            return '';
        if (password.length < 6)
            return 'text-danger';
        if (password.length < 8)
            return 'text-warning';
        return 'text-success';
    };
    const getPasswordStrengthText = () => {
        const password = formData.password;
        if (password.length === 0)
            return '';
        if (password.length < 6)
            return 'Too short';
        if (password.length < 8)
            return 'Weak';
        return 'Strong';
    };
    if (success) {
        return (_jsx("div", { className: "container mt-4", children: _jsx("div", { className: "row justify-content-center", children: _jsx("div", { className: "col-md-6 col-lg-5", children: _jsx("div", { className: "card shadow", children: _jsxs("div", { className: "card-body p-5", children: [_jsxs("div", { className: "text-center mb-4", children: [_jsx("div", { className: "mb-3", children: _jsx("i", { className: "fas fa-check-circle text-success", style: { fontSize: '3rem' } }) }), _jsx("h1", { className: "h3 mb-3", children: "Password Reset Successful!" }), _jsx("p", { className: "text-muted", children: "Your password has been changed successfully. You can now log in with your new password." })] }), _jsxs("div", { className: "text-center", children: [_jsxs("div", { className: "alert alert-success", children: [_jsx("i", { className: "fas fa-info-circle me-2" }), "Redirecting to login page in 3 seconds..."] }), _jsx(Link, { to: "/login", className: "btn btn-primary", children: "Go to Login Now" })] })] }) }) }) }) }));
    }
    return (_jsx("div", { className: "container mt-4", children: _jsx("div", { className: "row justify-content-center", children: _jsx("div", { className: "col-md-6 col-lg-5", children: _jsx("div", { className: "card shadow", children: _jsxs("div", { className: "card-body p-5", children: [_jsxs("div", { className: "text-center mb-4", children: [_jsxs("h1", { className: "h3 mb-3", children: [_jsx("i", { className: "fas fa-lock me-2" }), "Set New Password"] }), _jsx("p", { className: "text-muted", children: "Create a strong password for your account." })] }), error && (_jsxs("div", { className: "alert alert-danger", role: "alert", children: [_jsx("i", { className: "fas fa-exclamation-triangle me-2" }), error] })), _jsxs("form", { onSubmit: handleSubmit, children: [_jsxs("div", { className: "mb-3", children: [_jsx("label", { htmlFor: "password", className: "form-label", children: "New Password" }), _jsxs("div", { className: "input-group", children: [_jsx("input", { type: showPassword ? 'text' : 'password', className: "form-control", id: "password", name: "password", value: formData.password, onChange: handleChange, required: true, placeholder: "Enter your new password", disabled: loading, minLength: 8 }), _jsx("button", { type: "button", className: "btn btn-outline-secondary", onClick: () => setShowPassword(!showPassword), children: _jsx("i", { className: `fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}` }) })] }), formData.password && (_jsxs("small", { className: `form-text ${getPasswordStrengthColor()}`, children: ["Password strength: ", getPasswordStrengthText()] }))] }), _jsxs("div", { className: "mb-3", children: [_jsx("label", { htmlFor: "confirmPassword", className: "form-label", children: "Confirm New Password" }), _jsx("input", { type: "password", className: "form-control", id: "confirmPassword", name: "confirmPassword", value: formData.confirmPassword, onChange: handleChange, required: true, placeholder: "Confirm your new password", disabled: loading }), formData.confirmPassword && formData.password !== formData.confirmPassword && (_jsx("small", { className: "form-text text-danger", children: "Passwords do not match" }))] }), _jsx("div", { className: "mb-4", children: _jsx("div", { className: "card bg-light", children: _jsx("div", { className: "card-body py-2", children: _jsxs("small", { className: "text-muted", children: [_jsx("strong", { children: "Password Requirements:" }), _jsxs("ul", { className: "mb-0 mt-1", children: [_jsx("li", { children: "At least 8 characters long" }), _jsx("li", { children: "Mix of letters, numbers, and symbols recommended" })] })] }) }) }) }), _jsx("button", { type: "submit", className: "btn btn-primary w-100 mb-3", disabled: loading || !formData.password || !formData.confirmPassword || formData.password !== formData.confirmPassword, children: loading ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "spinner-border spinner-border-sm me-2", role: "status" }), "Resetting Password..."] })) : (_jsxs(_Fragment, { children: [_jsx("i", { className: "fas fa-key me-2" }), "Reset Password"] })) }), _jsx("div", { className: "text-center", children: _jsxs(Link, { to: "/login", className: "text-decoration-none", children: [_jsx("i", { className: "fas fa-arrow-left me-1" }), "Back to Login"] }) })] })] }) }) }) }) }));
};
export default ResetPassword;
